import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { Role, Permission } from '../../types';
import { getRole, updateRole, getPermissions, createRole } from '../../utils/auth/authService';
import RoleViewMode from '../../components/auth/RoleViewMode';
import RoleEditMode from '../../components/auth/RoleEditMode';

type Mode = 'view' | 'edit' | 'create';

const RoleDetailsPage: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreating = id === 'new';
  const [mode, setMode] = useState<Mode>(isCreating ? 'create' : 'view');
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [permissionsWarning, setPermissionsWarning] = useState('');

  // Edit/Create form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPermissions, setEditPermissions] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    try {
      setPermissionsWarning('');

      // If creating new role, only load permissions
      if (isCreating) {
        const permissionsData = await getPermissions();
        setPermissions(permissionsData);
        setLoading(false);
        return;
      }
      
      // Otherwise load existing role and permissions
      const roleData = await getRole(id);
      setRole(roleData);
      setEditName(roleData.name);
      setEditDescription(roleData.description || '');
      setEditPermissions(roleData.permissions);

      try {
        const permissionsData = await getPermissions();
        setPermissions(permissionsData);
      } catch (permErr) {
        setPermissions([]);
        setPermissionsWarning('Role loaded, but permission details could not be loaded.');
        console.error('Failed to load permissions:', permErr);
      }
    } catch (err) {
      setError('Failed to load role');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (role?.is_system_role) {
      alert('Cannot edit system roles');
      return;
    }
    setMode('edit');
  };

  const handleCancel = () => {
    if (isCreating) {
      navigate('/auth/roles');
      return;
    }
    if (role) {
      setEditName(role.name);
      setEditDescription(role.description || '');
      setEditPermissions(role.permissions);
    }
    setError('');
    setMode('view');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      if (isCreating) {
        // Create new role
        if (!editName.trim()) {
          setError('Role name is required');
          setSaving(false);
          return;
        }
        
        const newRole = await createRole({
          name: editName.trim(),
          description: editDescription || undefined,
          permissions: editPermissions,
        });
        
        // Navigate to the newly created role's detail page
        navigate(`/auth/roles/${newRole.role_id}`);
      } else {
        // Update existing role
        if (!id || !role) return;
        
        const updates = {
          description: editDescription || undefined,
          permissions: editPermissions,
        };
        const updatedRole = await updateRole(id, updates);
        setRole(updatedRole);
        setMode('view');
      }
    } catch (err) {
      setError(isCreating ? 'Failed to create role' : 'Failed to save role');
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading...
      </div>
    );
  }

  if (!role && !isCreating) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: theme.text, marginBottom: 16 }}>
          Role not found
        </div>
        <button
          onClick={() => navigate('/auth/roles')}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          Back to Roles
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/auth/roles')}
            style={{
              padding: 8,
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 style={{ color: theme.text, fontSize: 24, margin: 0, textTransform: 'uppercase' }}>
            {isCreating ? 'Create Role' : role?.name}
          </h1>
          {role?.is_system_role && (
            <span style={{
              padding: '4px 8px',
              background: '#e0f2fe',
              color: '#0369a1',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
            }}>
              System Role
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {mode === 'view' ? (
            <button
              onClick={handleEdit}
              disabled={role?.is_system_role}
              style={{
                padding: '10px 20px',
                background: role?.is_system_role ? theme.background : theme.buttonBackground,
                color: role?.is_system_role ? theme.textSecondary : theme.buttonText,
                border: 'none',
                borderRadius: 4,
                cursor: role?.is_system_role ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 500,
                opacity: role?.is_system_role ? 0.5 : 1,
              }}
            >
              <Edit size={18} />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <X size={18} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  background: theme.buttonBackground,
                  color: theme.buttonText,
                  border: 'none',
                  borderRadius: 4,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
        }}>
          {error}
        </div>
      )}

      {permissionsWarning && (
        <div style={{
          background: '#fff7ed',
          color: '#9a3412',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fdba74',
        }}>
          {permissionsWarning}
        </div>
      )}

      {/* Content */}
      {mode === 'view' ? (
        <RoleViewMode role={role!} permissions={permissions} theme={theme} />
      ) : (
        <RoleEditMode
          role={role}
          permissions={permissions}
          theme={theme}
          editName={editName}
          editDescription={editDescription}
          editPermissions={editPermissions}
          onNameChange={setEditName}
          onDescriptionChange={setEditDescription}
          onPermissionsChange={setEditPermissions}
        />
      )}
    </div>
  );
};

export default RoleDetailsPage;
